import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Empty, Flex, Progress, Tooltip, Typography } from 'antd';
import {
	LeftOutlined,
	MenuFoldOutlined,
	MenuUnfoldOutlined,
	RightOutlined,
} from '@ant-design/icons';
import { RiFocus3Line } from 'react-icons/ri';
import { Balloon } from '../../interfaces/Ballooning';
import {
	nextInQueue,
	reviewPosition,
	ReviewPosition,
	reviewQueue,
} from '../../utils/balloonReview';
import BalloonQuickEdit, { BalloonMeasurementUpdate } from '../BalloonQuickEdit';
import BalloonReviewActions from '../BalloonReviewActions';
import { StrategyOption } from '../BalloonTable/measurementFields';

interface BalloonReviewPanelProps {
	balloons: Balloon[];
	activeBalloonId: string | null;
	/** Make the balloon active, switch to its page and center it in the viewer. */
	onSelect: (balloon: Balloon) => void;
	isEditable: (balloon: Balloon) => boolean;
	onChange: (id: string, updates: BalloonMeasurementUpdate) => void;
	onStrategyChange?: (id: string, value: string, option: StrategyOption) => void;
	onToolChange?: (id: string, value: string) => void;
	keyenceBuildingId?: string | null;
	onApprove: (id: string) => Promise<boolean>;
	onDelete: (id: string) => void;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

const PANEL_WIDTH = 300;

const BalloonReviewPanel: React.FC<BalloonReviewPanelProps> = ({
	balloons,
	activeBalloonId,
	onSelect,
	isEditable,
	onChange,
	onStrategyChange,
	onToolChange,
	keyenceBuildingId,
	onApprove,
	onDelete,
	open,
	onOpenChange,
}) => {
	const queue = useMemo(() => reviewQueue(balloons), [balloons]);
	const autoCount = useMemo(
		() => balloons.filter((b) => b.is_auto_generated).length,
		[balloons]
	);
	const [currentId, setCurrentId] = useState<string | null>(null);
	// An already-reviewed AI balloon picked in the viewer or table, shown read-only.
	const [viewedId, setViewedId] = useState<string | null>(null);
	const [approving, setApproving] = useState(false);
	// Where the balloon under review sat, so the next one can be found after it leaves.
	const anchor = useRef<ReviewPosition | null>(null);

	const index = queue.findIndex((b) => b.id === currentId);
	const current = index >= 0 ? queue[index] : null;
	const viewed =
		(viewedId && balloons.find((b) => b.id === viewedId && b.is_auto_generated)) || null;
	const shown = viewed ?? current;

	const select = (balloon: Balloon) => {
		setViewedId(null);
		setCurrentId(balloon.id);
		anchor.current = reviewPosition(balloon);
		onSelect(balloon);
	};

	// A balloon picked in the viewer or table becomes the one under review, or is shown
	// read-only when it has been reviewed already.
	useEffect(() => {
		const picked = balloons.find((b) => b.id === activeBalloonId);
		if (!picked?.is_auto_generated) return;
		if (queue.some((b) => b.id === picked.id)) {
			setViewedId(null);
			if (picked.id !== currentId) {
				setCurrentId(picked.id);
				anchor.current = reviewPosition(picked);
			}
		} else {
			setViewedId(picked.id);
		}
	}, [activeBalloonId]);

	// From a reviewed balloon, Previous and Next go to the unreviewed ones around it.
	const viewedPosition = viewed ? reviewPosition(viewed) : null;
	const isAfterViewed = (b: Balloon) =>
		!!viewedPosition &&
		((b.ballooning_page_number ?? 0) > viewedPosition.page ||
			((b.ballooning_page_number ?? 0) === viewedPosition.page &&
				b.balloon_number > viewedPosition.number));
	const previous = viewed
		? [...queue].reverse().find((b) => !isAfterViewed(b)) ?? null
		: index > 0
			? queue[index - 1]
			: null;
	const next = viewed
		? queue.find(isAfterViewed) ?? null
		: index >= 0 && index < queue.length - 1
			? queue[index + 1]
			: null;

	// Approved, deleted or never set: move on to the next balloon in sequence.
	useEffect(() => {
		if (!open || current) return;
		const next = nextInQueue(queue, anchor.current);
		if (next) select(next);
		else setCurrentId(null);
	}, [open, current, queue]);

	if (!autoCount) return null;

	if (!open) {
		return (
			<Flex
				vertical
				align="center"
				gap={8}
				className="h-full overflow-hidden border bg-white"
				style={{ width: 36, paddingTop: 8 }}
			>
				<Tooltip title="Show balloon review" placement="left">
					<Button
						size="small"
						type="text"
						icon={<MenuFoldOutlined />}
						onClick={() => onOpenChange(true)}
						aria-label="Show balloon review"
					/>
				</Tooltip>
				{queue.length > 0 && (
					<Typography.Text type="warning" strong style={{ fontSize: 12 }}>
						{queue.length}
					</Typography.Text>
				)}
			</Flex>
		);
	}

	const reviewed = autoCount - queue.length;
	const editable = current && !viewed ? isEditable(current) : false;
	const reviewing = !!current && !viewed;

	const handleApprove = async () => {
		if (!current || viewed) return;
		setApproving(true);
		try {
			await onApprove(current.id);
		} finally {
			setApproving(false);
		}
	};

	return (
		<Flex
			vertical
			className="h-full overflow-hidden border bg-white"
			style={{ width: PANEL_WIDTH, minWidth: PANEL_WIDTH }}
			data-testid="balloon-review-panel"
		>
			<Flex
				justify="space-between"
				align="center"
				className="border-b"
				style={{ padding: '8px 12px' }}
			>
				<Typography.Text strong>Balloon review</Typography.Text>
				<Flex gap={4}>
					<Tooltip title="Center balloon in view">
						<Button
							size="small"
							type="text"
							icon={<RiFocus3Line />}
							disabled={!shown}
							onClick={() => shown && onSelect(shown)}
							aria-label="Center balloon in view"
							data-testid="balloon-review-center"
						/>
					</Tooltip>
					<Button
						size="small"
						type="text"
						icon={<MenuUnfoldOutlined />}
						onClick={() => onOpenChange(false)}
						aria-label="Hide balloon review"
					/>
				</Flex>
			</Flex>

			<div style={{ padding: '8px 12px 0' }}>
				<Typography.Text type="secondary" style={{ fontSize: 12 }}>
					{reviewed} of {autoCount} AI balloons reviewed
				</Typography.Text>
				<Progress
					percent={Math.round((reviewed / autoCount) * 100)}
					showInfo={false}
					size="small"
				/>
			</div>

			<div className="flex-1 overflow-auto" style={{ padding: '8px 12px' }}>
				{shown ? (
					<BalloonQuickEdit
						key={shown.id}
						variant="panel"
						balloon={shown}
						editable={editable}
						onChange={(updates) => onChange(shown.id, updates)}
						onStrategyChange={
							onStrategyChange &&
							((value, option) => onStrategyChange(shown.id, value, option))
						}
						onToolChange={onToolChange && ((value) => onToolChange(shown.id, value))}
						keyenceBuildingId={keyenceBuildingId}
					/>
				) : (
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description="Every AI balloon has been reviewed."
					/>
				)}
			</div>

			<Flex vertical gap={8} className="border-t" style={{ padding: '10px 12px' }}>
				<BalloonReviewActions
					balloonNumber={current?.balloon_number}
					onApprove={handleApprove}
					approving={approving}
					approveDisabled={!reviewing}
					approveLabel={viewed ? 'Reviewed' : 'Approve'}
					onDelete={() => current && onDelete(current.id)}
					deleteDisabled={!reviewing || !editable}
					testIdPrefix="balloon-review"
				/>
				<Flex justify="space-between" align="center">
					<Button
						icon={<LeftOutlined />}
						disabled={!previous}
						onClick={() => previous && select(previous)}
						data-testid="balloon-review-previous"
					>
						Previous
					</Button>
					<Typography.Text type="secondary" style={{ fontSize: 12 }}>
						{viewed ? 'Reviewed' : current ? `${index + 1} / ${queue.length}` : '—'}
					</Typography.Text>
					<Button
						disabled={!next}
						onClick={() => next && select(next)}
						data-testid="balloon-review-next"
					>
						Next <RightOutlined />
					</Button>
				</Flex>
			</Flex>
		</Flex>
	);
};

export default BalloonReviewPanel;
